import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { supabase } from '@/api/supabase.js'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null)
  const session = ref(null)
  const loading = ref(false)

  // Getters
  const isLoggedIn = computed(() => !!session.value?.access_token)
  const isSuperAdmin = computed(() => user.value?.app_metadata?.role === 'super_admin')
  const isAdmin = computed(() => {
    const role = user.value?.app_metadata?.role
    return role === 'admin' || role === 'super_admin'
  })
  const userRole = computed(() => user.value?.app_metadata?.role || '')
  const userEmail = computed(() => user.value?.email || '')

  // Actions
  async function login(email, password) {
    loading.value = true
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      if (error) throw error

      // 校验是否为管理员
      const role = data.user?.app_metadata?.role
      if (!['admin', 'super_admin'].includes(role)) {
        await supabase.auth.signOut()
        throw new Error('非管理员账号，无权登录后台系统')
      }

      session.value = data.session
      user.value = data.user
      return { success: true }
    } catch (err) {
      return { success: false, message: err.message }
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    await supabase.auth.signOut()
    session.value = null
    user.value = null
  }

  async function restoreSession() {
    try {
      const { data, error } = await supabase.auth.getSession()
      if (error || !data.session) return false

      const role = data.session.user?.app_metadata?.role
      if (!['admin', 'super_admin'].includes(role)) return false

      session.value = data.session
      user.value = data.session.user
      return true
    } catch {
      return false
    }
  }

  async function refreshProfile() {
    const { data } = await supabase.auth.getUser()
    if (data.user) user.value = data.user
  }

  return {
    user,
    session,
    loading,
    isLoggedIn,
    isSuperAdmin,
    isAdmin,
    userRole,
    userEmail,
    login,
    logout,
    restoreSession,
    refreshProfile,
  }
})
